import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const BAND_HEIGHT = 50;
const BAND_GAP = 4;
const MARGIN = { top: 10, right: 20, bottom: 40, left: 120 };
const NEG_COLOR = '#dc2626';

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '12px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '0',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

export default function HorizonChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.fields || data.fields.length === 0 || !data.dates || data.dates.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 600;
    const width = Math.max(600, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const totalHeight = MARGIN.top + data.fields.length * (BAND_HEIGHT + BAND_GAP) - BAND_GAP + MARGIN.bottom;

    svg.attr('width', width).attr('height', totalHeight);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Shared x scale across all rows
    const x = d3.scalePoint()
      .domain(data.dates)
      .range([0, innerW]);

    // Define a unique clipPath ID prefix to avoid collisions
    const clipId = 'horizon-clip-' + Math.random().toString(36).slice(2, 8);

    const defs = svg.append('defs');

    // Draw each field as a horizon band row
    data.fields.forEach((field, fi) => {
      const rowY = fi * (BAND_HEIGHT + BAND_GAP);
      const rowG = g.append('g').attr('transform', `translate(0, ${rowY})`);
      const halfBand = BAND_HEIGHT / 2;

      // Compute threshold: max absolute deviation / 2
      const maxAbs = d3.max(field.values, (v) => Math.abs(v)) || 1;
      const threshold = maxAbs / 2;

      // Y scale: maps deviation value to pixels within the band
      // 0 deviation = center of band (halfBand)
      // +threshold = top of band (0), -threshold = bottom of band (BAND_HEIGHT)
      const yScale = d3.scaleLinear()
        .domain([-threshold, threshold])
        .range([BAND_HEIGHT, 0])
        .clamp(false);

      // Build data points array
      const points = data.dates.map((date, i) => ({
        date,
        value: field.values[i] || 0,
      }));

      // Baseline (zero line) - dashed
      rowG.append('line')
        .attr('x1', 0)
        .attr('x2', innerW)
        .attr('y1', halfBand)
        .attr('y2', halfBand)
        .attr('stroke', '#d1d5db')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '3,3');

      // Area generator: baseline at center (halfBand), area goes toward value
      const areaGen = d3.area()
        .x((d) => x(d.date))
        .y0(halfBand)
        .y1((d) => yScale(d.value));

      // --- Positive bands ---
      // Band 1 positive: clip to [0, halfBand] (the top half of the row)
      const posClip1Id = `${clipId}-pos1-${fi}`;
      defs.append('clipPath')
        .attr('id', posClip1Id)
        .append('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', innerW)
        .attr('height', halfBand);

      rowG.append('path')
        .datum(points)
        .attr('d', areaGen)
        .attr('fill', field.color)
        .attr('fill-opacity', 0.3)
        .attr('stroke', 'none')
        .attr('clip-path', `url(#${posClip1Id})`);

      // Band 2 positive: the area that extends beyond the top edge (values > threshold)
      // Fold it back by shifting the area down by halfBand
      const posClip2Id = `${clipId}-pos2-${fi}`;
      defs.append('clipPath')
        .attr('id', posClip2Id)
        .append('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', innerW)
        .attr('height', halfBand);

      // For the folded band, we create a new area generator that maps
      // values in [threshold, 2*threshold] into the same visual band [0, halfBand]
      const areaGenPosFold = d3.area()
        .x((d) => x(d.date))
        .y0(halfBand)
        .y1((d) => {
          const excess = Math.max(0, d.value - threshold);
          // Map excess into the band: 0 excess = halfBand (baseline), threshold excess = 0 (top)
          return halfBand - (excess / threshold) * halfBand;
        });

      rowG.append('path')
        .datum(points)
        .attr('d', areaGenPosFold)
        .attr('fill', field.color)
        .attr('fill-opacity', 0.6)
        .attr('stroke', 'none')
        .attr('clip-path', `url(#${posClip2Id})`);

      // --- Negative bands ---
      // Band 1 negative: clip to [halfBand, BAND_HEIGHT] (the bottom half)
      const negClip1Id = `${clipId}-neg1-${fi}`;
      defs.append('clipPath')
        .attr('id', negClip1Id)
        .append('rect')
        .attr('x', 0)
        .attr('y', halfBand)
        .attr('width', innerW)
        .attr('height', halfBand);

      rowG.append('path')
        .datum(points)
        .attr('d', areaGen)
        .attr('fill', NEG_COLOR)
        .attr('fill-opacity', 0.3)
        .attr('stroke', 'none')
        .attr('clip-path', `url(#${negClip1Id})`);

      // Band 2 negative: folded excess below -threshold
      const negClip2Id = `${clipId}-neg2-${fi}`;
      defs.append('clipPath')
        .attr('id', negClip2Id)
        .append('rect')
        .attr('x', 0)
        .attr('y', halfBand)
        .attr('width', innerW)
        .attr('height', halfBand);

      const areaGenNegFold = d3.area()
        .x((d) => x(d.date))
        .y0(halfBand)
        .y1((d) => {
          const excess = Math.max(0, -d.value - threshold);
          // Map excess into the bottom band: 0 excess = halfBand, threshold excess = BAND_HEIGHT
          return halfBand + (excess / threshold) * halfBand;
        });

      rowG.append('path')
        .datum(points)
        .attr('d', areaGenNegFold)
        .attr('fill', NEG_COLOR)
        .attr('fill-opacity', 0.6)
        .attr('stroke', 'none')
        .attr('clip-path', `url(#${negClip2Id})`);

      // Left label
      rowG.append('text')
        .attr('x', -10)
        .attr('y', halfBand)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'central')
        .attr('font-size', '12px')
        .attr('font-weight', 600)
        .attr('fill', field.color)
        .text(field.label);
    });

    // Shared X axis at the bottom
    const xAxisY = data.fields.length * (BAND_HEIGHT + BAND_GAP) - BAND_GAP;
    g.append('g')
      .attr('transform', `translate(0, ${xAxisY})`)
      .call(
        d3.axisBottom(x)
          .tickSize(0)
          .tickFormat((d) => formatDateLabel(d))
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.25em');

    g.selectAll('.domain').attr('stroke', '#e5e7eb');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Crosshair line (spans all rows, initially hidden)
    const crosshair = g.append('line')
      .attr('y1', 0)
      .attr('y2', xAxisY)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    // Invisible overlay columns for tooltip hover
    const colWidth = data.dates.length > 1
      ? innerW / (data.dates.length - 1)
      : innerW;

    data.dates.forEach((date, i) => {
      const cx = x(date);
      const halfW = colWidth / 2;

      g.append('rect')
        .attr('x', cx - halfW)
        .attr('y', 0)
        .attr('width', colWidth)
        .attr('height', xAxisY)
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          crosshair
            .attr('x1', cx)
            .attr('x2', cx)
            .style('display', null);

          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            date,
            values: data.fields.map((field) => ({
              key: field.key,
              label: field.label,
              color: field.color,
              value: field.values[i],
            })),
          });
        })
        .on('mouseleave', () => {
          crosshair.style('display', 'none');
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Empty state
  if (!data || !data.fields || data.fields.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Price Deviation Horizon</div>
          <div style={styles.subtitle}>
            Compact view of price deviations from baseline across all fields
          </div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No horizon data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Price Deviation Horizon</div>
        <div style={styles.subtitle}>
          Compact view of price deviations from baseline across all fields
        </div>
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        {data.fields.map((field) => (
          <div key={field.key} style={styles.legendItem}>
            <svg width="14" height="14">
              <rect x="0" y="0" width="14" height="14" fill={field.color} fillOpacity="0.5" rx="2" />
            </svg>
            <span>{field.label}</span>
          </div>
        ))}
        <div style={styles.legendItem}>
          <svg width="14" height="14">
            <rect x="0" y="0" width="14" height="14" fill="#2563eb" fillOpacity="0.3" rx="2" />
          </svg>
          <span>Positive deviation</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="14" height="14">
            <rect x="0" y="0" width="14" height="14" fill={NEG_COLOR} fillOpacity="0.3" rx="2" />
          </svg>
          <span>Negative deviation</span>
        </div>
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {formatDateFull(tooltip.date)}
          </div>
          {tooltip.values.map((v) => (
            <div
              key={v.key}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '16px',
                marginBottom: '2px',
              }}
            >
              <span style={{ color: v.color, fontWeight: 600 }}>{v.label}</span>
              <span
                style={{
                  fontWeight: 600,
                  color: v.value >= 0 ? '#16a34a' : '#dc2626',
                }}
              >
                {v.value >= 0 ? '+' : ''}{v.value != null ? v.value.toFixed(2) : '--'}%
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
